import { useState, useEffect } from "react";
import { CheckCircle, ExternalLink, Loader2, Plus, RefreshCw, Star, Trash2, Truck, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../utils/api";

const API = `/api`;

function ReviewTrackerSection({ reviewTracker, onUpdate }) {
  const [marking, setMarking] = useState(null);
  const [googleReviews, setGoogleReviews] = useState({ reviews: [], stats: { total: 0, average_rating: 0, five_star: 0, four_star: 0, recent_count: 0 } });
  const [showAddReview, setShowAddReview] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reviewForm, setReviewForm] = useState({ reviewer_name: "", rating: 5, review_text: "", review_date: "" });
  const GOOGLE_REVIEW_URL = "https://search.google.com/local/reviews?placeid=ChIJY6jc-ZVbkWsR_tJ8nLuVa2I";

  // Fetch Google reviews
  const fetchGoogleReviews = async () => {
    try {
      const res = await api.get(`${API}/admin/google-reviews`);
      setGoogleReviews(res.data);
    } catch (err) {
      console.error("Failed to fetch Google reviews");
    }
  };

  useEffect(() => {
    fetchGoogleReviews();
  }, []);

  const markAsReviewed = async (type, id) => {
    setMarking(id);
    try {
      await api.patch(`${API}/dashboard/review-tracker/${type}/${id}`);
      toast.success("Marked as reviewed");
      onUpdate();
    } catch (err) {
      toast.error("Failed to update");
    } finally {
      setMarking(null);
    }
  };

  const handleRefresh = async () => {
    window.open(GOOGLE_REVIEW_URL, '_blank');
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`${API}/admin/google-reviews/sync`);
      if (res.data.api_key_configured === false) {
        // Open Google reviews in new tab for easy copy/paste
        window.open(GOOGLE_REVIEW_URL, '_blank');
        toast.info(
          <div>
            <strong>Google Reviews opened in new tab!</strong>
            <p className="text-sm mt-1">Copy the reviews you want and click "Add Manually" to add them here.</p>
          </div>,
          { duration: 6000 }
        );
      } else if (res.data.error) {
        toast.error(`Sync failed: ${res.data.error}`);
      } else if (res.data.synced > 0) {
        toast.success(`Synced ${res.data.synced} new reviews from Google!`);
        await fetchGoogleReviews();
      } else {
        toast.info("No new reviews to sync - all reviews already added");
        await fetchGoogleReviews();
      }
    } catch (err) {
      toast.error("Failed to sync reviews");
    } finally {
      setSyncing(false);
    }
  };

  const addGoogleReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.reviewer_name.trim()) {
      toast.error("Reviewer name is required");
      return;
    }
    try {
      await api.post(`${API}/admin/google-reviews`, reviewForm);
      toast.success("Review added");
      setShowAddReview(false);
      setReviewForm({ reviewer_name: "", rating: 5, review_text: "", review_date: "" });
      fetchGoogleReviews();
    } catch (err) {
      toast.error("Failed to add review");
    }
  };

  const deleteGoogleReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`${API}/admin/google-reviews/${id}`);
      toast.success("Review deleted");
      fetchGoogleReviews();
    } catch (err) {
      toast.error("Failed to delete review");
    }
  };

  const unreviewedJobs = reviewTracker.completed_jobs.filter(j => !j.review_left);
  const reviewedJobs = reviewTracker.completed_jobs.filter(j => j.review_left);

  // Render stars
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} size={14} className={i < rating ? "text-[#f59e0b] fill-[#f59e0b]" : "text-zinc-700"} />
    ));
  };

  return (
    <div className="space-y-6" data-testid="review-tracker">
      {/* Google Reviews Section */}
      <div className="bg-[#09090b] border border-white/5">
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#f59e0b]/10 flex items-center justify-center">
                <Star size={24} className="text-[#f59e0b]" />
              </div>
              <div>
                <h3 className="font-heading text-lg uppercase tracking-wide text-white">Google Reviews</h3>
                <p className="text-zinc-500 font-body text-sm">Reviews from your Google Business Profile</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Stats */}
              <div className="flex gap-4 text-center mr-4">
                <div>
                  <span className="block font-heading text-2xl text-[#f59e0b]">{googleReviews.stats.average_rating || "—"}</span>
                  <span className="text-xs text-zinc-600 uppercase tracking-wider">Avg Rating</span>
                </div>
                <div>
                  <span className="block font-heading text-2xl text-white">{googleReviews.stats.total}</span>
                  <span className="text-xs text-zinc-600 uppercase tracking-wider">Total</span>
                </div>
                <div>
                  <span className="block font-heading text-2xl text-[#22c55e]">{googleReviews.stats.five_star}</span>
                  <span className="text-xs text-zinc-600 uppercase tracking-wider">5-Star</span>
                </div>
              </div>
              {/* Action buttons */}
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 text-xs uppercase font-body bg-[#22c55e] text-white border-none hover:bg-[#22c55e]/80 transition-colors cursor-pointer disabled:opacity-50"
                title="Sync reviews from Google (requires API key)"
              >
                {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Sync from Google
              </button>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-4 py-2 text-xs uppercase font-body border border-[#3b82f6]/30 text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-colors cursor-pointer bg-transparent"
                title="View all reviews on Google"
              >
                <ExternalLink size={14} />
                See All Reviews
              </button>
              <button
                onClick={() => setShowAddReview(true)}
                className="flex items-center gap-2 px-4 py-2 text-xs uppercase font-body bg-[#f59e0b] text-white border-none hover:bg-[#f59e0b]/80 transition-colors cursor-pointer"
              >
                <Plus size={14} /> Add Manually
              </button>
            </div>
          </div>
        </div>

        {/* Reviews list */}
        <div className="p-4 max-h-[400px] overflow-y-auto">
          {googleReviews.reviews.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Star size={32} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 font-body text-sm mb-2">No Google reviews added yet</p>
              <p className="text-zinc-600 font-body text-xs mb-4">Click "Sync from Google" to open your reviews, then copy and add them here</p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={handleSync}
                  className="text-xs px-3 py-1.5 bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30 hover:bg-[#22c55e]/30 transition-colors cursor-pointer"
                >
                  Open Google Reviews
                </button>
                <span className="text-zinc-700">then</span>
                <button
                  onClick={() => setShowAddReview(true)}
                  className="text-xs px-3 py-1.5 bg-[#f59e0b]/20 text-[#f59e0b] border border-[#f59e0b]/30 hover:bg-[#f59e0b]/30 transition-colors cursor-pointer"
                >
                  Add Manually
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {googleReviews.reviews.map((review) => (
                <div key={review.id} className="bg-white/5 p-4 border-l-4 border-[#f59e0b]">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-heading text-sm text-white">{review.reviewer_name}</span>
                        <div className="flex gap-0.5">{renderStars(review.rating)}</div>
                        {review.review_date && (
                          <span className="text-xs text-zinc-600 font-mono">{review.review_date}</span>
                        )}
                      </div>
                      {review.review_text && (
                        <p className="text-zinc-400 font-body text-sm">{review.review_text}</p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteGoogleReview(review.id)}
                      className="p-1 text-zinc-600 hover:text-red-500 transition-colors bg-transparent border-none cursor-pointer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Review Modal */}
      {showAddReview && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowAddReview(false)}>
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="font-heading text-sm uppercase tracking-wide text-white">Add Google Review</h3>
              <button onClick={() => setShowAddReview(false)} className="text-zinc-500 hover:text-white transition-colors bg-transparent border-none cursor-pointer">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={addGoogleReview} className="p-4 space-y-4">
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-2">Reviewer Name *</label>
                <input
                  value={reviewForm.reviewer_name}
                  onChange={(e) => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })}
                  placeholder="e.g., John S"
                  className="w-full bg-transparent border-b border-white/10 text-white font-body text-sm py-2 px-0 outline-none focus:border-[#f59e0b]/50 placeholder:text-zinc-700"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                      className="p-2 bg-transparent border-none cursor-pointer"
                    >
                      <Star
                        size={24}
                        className={star <= reviewForm.rating ? "text-[#f59e0b] fill-[#f59e0b]" : "text-zinc-700"}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-2">Review Date (optional)</label>
                <input
                  value={reviewForm.review_date}
                  onChange={(e) => setReviewForm({ ...reviewForm, review_date: e.target.value })}
                  placeholder="e.g., 2 weeks ago"
                  className="w-full bg-transparent border-b border-white/10 text-white font-body text-sm py-2 px-0 outline-none focus:border-[#f59e0b]/50 placeholder:text-zinc-700"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase text-zinc-400 font-body mb-2">Review Text (optional)</label>
                <textarea
                  value={reviewForm.review_text}
                  onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                  placeholder="What did they say?"
                  rows={3}
                  className="w-full bg-transparent border border-white/10 text-white font-body text-sm p-2 outline-none focus:border-[#f59e0b]/50 placeholder:text-zinc-700 resize-none"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowAddReview(false)} className="flex-1 py-2 text-xs uppercase font-body border border-white/10 text-zinc-500 hover:text-white transition-colors cursor-pointer bg-transparent">Cancel</button>
                <button type="submit" className="flex-1 py-2 text-xs uppercase font-body bg-[#f59e0b] text-white border-none hover:bg-[#f59e0b]/80 transition-colors cursor-pointer">Add Review</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Job Review Tracker Section */}
      <div className="bg-[#09090b] border border-white/5 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-1">Customer Review Tracker</h3>
            <p className="text-zinc-500 font-body text-sm">Track which customers have left reviews</p>
          </div>
          <div className="flex gap-6 text-center">
            <div>
              <span className="block font-heading text-2xl text-white">{reviewTracker.total}</span>
              <span className="text-xs text-zinc-600 uppercase tracking-wider">Completed</span>
            </div>
            <div>
              <span className="block font-heading text-2xl text-[#22c55e]">{reviewTracker.reviews_marked}</span>
              <span className="text-xs text-zinc-600 uppercase tracking-wider">Reviewed</span>
            </div>
            <div>
              <span className="block font-heading text-2xl text-[#f59e0b]">{reviewTracker.total - reviewTracker.reviews_marked}</span>
              <span className="text-xs text-zinc-600 uppercase tracking-wider">Pending</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pending reviews */}
      {unreviewedJobs.length > 0 && (
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase text-zinc-500 font-body mb-3">Awaiting Review ({unreviewedJobs.length})</h4>
          <div className="space-y-[1px] bg-white/5">
            {unreviewedJobs.map((job) => (
              <div key={`${job.type}-${job.id}`} className="bg-[#09090b] p-5 flex items-center justify-between" data-testid={`review-item-${job.id}`}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#f59e0b]/10 border border-[#f59e0b]/20 flex items-center justify-center">
                    <Star size={16} className="text-[#f59e0b]" />
                  </div>
                  <div>
                    <h4 className="font-heading text-base uppercase tracking-wide text-white">{job.name}</h4>
                    <p className="text-zinc-500 font-mono text-sm">{job.vehicle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-zinc-600 text-xs font-mono">
                    {job.completed_at ? new Date(job.completed_at).toLocaleDateString("en-AU") : ""}
                  </span>
                  <button
                    onClick={() => markAsReviewed(job.type, job.id)}
                    disabled={marking === job.id}
                    data-testid={`mark-reviewed-${job.id}`}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs tracking-[0.05em] uppercase font-body border border-[#22c55e]/30 text-[#22c55e] hover:bg-[#22c55e]/10 transition-colors cursor-pointer bg-transparent disabled:opacity-50"
                  >
                    {marking === job.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                    Mark Reviewed
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviewed */}
      {reviewedJobs.length > 0 && (
        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase text-zinc-500 font-body mb-3">Reviewed ({reviewedJobs.length})</h4>
          <div className="space-y-[1px] bg-white/5">
            {reviewedJobs.map((job) => (
              <div key={`${job.type}-${job.id}`} className="bg-[#09090b] p-5 flex items-center justify-between opacity-60">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#22c55e]/10 border border-[#22c55e]/20 flex items-center justify-center">
                    <CheckCircle size={16} className="text-[#22c55e]" />
                  </div>
                  <div>
                    <h4 className="font-heading text-base uppercase tracking-wide text-white">{job.name}</h4>
                    <p className="text-zinc-500 font-mono text-sm">{job.vehicle}</p>
                  </div>
                </div>
                <span className="text-[#22c55e] text-xs uppercase tracking-wider font-body">Reviewed</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {reviewTracker.total === 0 && (
        <div className="bg-[#09090b] p-12 text-center">
          <Truck size={32} className="text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500 font-body">No completed jobs yet. Customer tracking will appear here once jobs are marked as completed.</p>
        </div>
      )}
    </div>
  );
}

export { ReviewTrackerSection };
